import CircularProgress from "@material-ui/core/CircularProgress";
import React, { FC, useCallback, useEffect, useState } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";

import CampaignTemplateCard from "../../components/redesign/modules/CampaignTemplateCard";
import CampaignTemplatesEmptyState from "../../components/redesign/modules/CampaignTemplatesEmptyState";
import CampaignTemplatesToolbar from "../../components/redesign/modules/CampaignTemplatesToolbar";
import api from "../../api";
import { CampaignTemplate } from "../../types/campaignTypes";
import { ROUTE_PATHS } from "../../types/RoutePaths";
import { ReduxApplicationState } from "../../types/ReduxTypes/ReduxApplicationState";
import { useGlobalSelectedAdvertiser } from "../../hooks/useGlobalSelectedAdvertiser";

const PageRoot = styled("div")({
  flex: 1,
  minHeight: 0,
});

const TemplatesGrid = styled("div")({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
  gap: 16,
});

const LoadingWrap = styled("div")(({ theme }: { theme: Theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 80,
  color: theme.palette.brand.gray400,
}));

const CampaignTemplatesPageRedesign: FC = () => {
  const history = useHistory();
  const user = useSelector((state: ReduxApplicationState) => state.user.currentUser);
  const { selectedAdvertiserId } = useGlobalSelectedAdvertiser();

  const [templates, setTemplates] = useState<CampaignTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchTemplates = useCallback(async () => {
    if (!user) return;

    if (!selectedAdvertiserId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const data = await api.getCampaignTemplatesByUser(user);
      setTemplates(
        (data as CampaignTemplate[]).filter(
          (t) => !t.selectedAdvertiser || t.selectedAdvertiser === selectedAdvertiserId,
        ),
      );
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    } finally {
      setIsLoading(false);
    }
  }, [user, selectedAdvertiserId]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleNewTemplate = () => {
    history.push(ROUTE_PATHS.CREATE_CAMPAIGN_TEMPLATE);
  };

  if (isLoading) {
    return (
      <PageRoot>
        <LoadingWrap>
          <CircularProgress size={32} />
        </LoadingWrap>
      </PageRoot>
    );
  }

  if (!templates.length) {
    return (
      <PageRoot>
        <CampaignTemplatesEmptyState onNewTemplate={handleNewTemplate} />
      </PageRoot>
    );
  }

  return (
    <PageRoot>
      <CampaignTemplatesToolbar onNewTemplate={handleNewTemplate} />
      <TemplatesGrid>
        {templates.map((template) => (
          <CampaignTemplateCard key={template.id} template={template} fetchTemplates={fetchTemplates} />
        ))}
      </TemplatesGrid>
    </PageRoot>
  );
};

export default CampaignTemplatesPageRedesign;
